import { z } from 'zod';
/**
 * What a database driver reports (knowledge doc section 6: "Every engine returns the
 * same shapes, so the central schema never depends on which databases servers run").
 *
 * Section 20 asked for this to be designed against all four engines before the first
 * driver was written, because a shape drawn from PostgreSQL alone would fit MySQL and
 * nothing else. It was drawn from what PostgreSQL 17, MySQL 8.4, MariaDB 11.4,
 * MongoDB 8.0 and Redis 8 actually return to a least-privileged account, and four
 * things in that output decided it:
 *
 * - **Not every engine accounts per database.** PostgreSQL, MySQL and MongoDB report
 *   bytes per database; Redis reports one memory figure for the whole instance and
 *   only a key count per numbered database. So `bytes` is nullable on a database,
 *   and the instance total is its own field that every engine can fill.
 * - **Disk and memory are different things to run out of.** Redis's size is RAM;
 *   the rest are on disk. `medium` says which, rather than letting a chart put the
 *   two on one axis as if they were the same resource.
 * - **What a user cannot see differs by engine.** PostgreSQL tells a role that a
 *   database exists and how big it is even without `CONNECT`, but will not let it
 *   list the tables inside. MySQL's `information_schema` hides the database
 *   entirely; its InnoDB statistics table shows every one, and nothing that is not
 *   InnoDB. So `unreadable` holds what the engine admitted to and would not open,
 *   and an empty list means "nothing the engine admitted to", never "everything was
 *   read".
 * - **Counts are estimates in two engines and exact in two.** `pg_class.reltuples`
 *   and InnoDB's `table_rows` are the planner's guesses (20,000 real rows reported
 *   as 16,004 in the capture); MongoDB's and Redis's counts are kept exactly. `items`
 *   is documented as an estimate rather than split into two fields nobody would
 *   choose between.
 *
 * Nothing here is sent over the network as it is: the `db_size` module turns it into
 * metrics. It is a schema rather than a bare type because a driver's output is built
 * from what a database server said, which is a boundary like any other.
 */
/** Where an engine's size lives. Redis is in memory; the others are on disk. */
export const DbMedium = z.enum(['disk', 'memory']);
/**
 * A table-like thing with a size of its own.
 *
 * `partitioned_table` is reported once with its partitions summed underneath it,
 * rather than as the partitions: a table split by day would otherwise fill all
 * twenty places with its own slices — Bartizo's own `metrics` table is exactly that.
 */
export const DbObjectKind = z.enum([
    'table',
    'partitioned_table',
    'materialized_view',
    'collection',
]);
/** Identifiers: 63 bytes in PostgreSQL, 64 in MySQL, 255 for a MongoDB namespace. */
const Identifier = z.string().min(1).max(256);
const Bytes = z.number().int().nonnegative();
const Count = z.number().int().nonnegative();
export const DbDatabaseSize = z.object({
    name: Identifier,
    /** Null when the engine does not account per database (Redis). */
    bytes: Bytes.nullable(),
    /** Rows, documents or keys; an estimate in PostgreSQL and InnoDB. Null when unknown. */
    items: Count.nullable(),
});
export const DbObjectSize = z.object({
    database: Identifier,
    /** Schema-qualified where the engine has schemas: `public.users`. */
    name: Identifier,
    kind: DbObjectKind,
    /** Everything the object occupies: data, indexes, and PostgreSQL's TOAST. */
    bytes: Bytes,
    items: Count.nullable(),
});
export const DbUnreadable = z.object({
    database: Identifier,
    /** For a person to act on: which privilege is missing, never a stack. */
    reason: z.string().min(1).max(300),
});
export const DbSizes = z.object({
    medium: DbMedium,
    /**
     * Everything the instance holds that this account can see. The sum of `databases`
     * for the three that account per database; Redis's `used_memory` for Redis.
     */
    totalBytes: Bytes,
    databases: z.array(DbDatabaseSize).max(1000),
    /** The largest, biggest first, across every readable database. */
    objects: z.array(DbObjectSize).max(100),
    unreadable: z.array(DbUnreadable).max(1000),
});
/**
 * Where this instance sits in replication. Null when the account is not allowed to
 * ask — MySQL wants `REPLICATION CLIENT` (MariaDB calls it `SLAVE MONITOR`) for it.
 */
export const DbReplicationRole = z.enum(['standalone', 'primary', 'replica']);
/**
 * Section 8.5's list. Every field is nullable because every engine is missing at
 * least one of them, and null says "this engine or this account cannot tell",
 * which a zero would get wrong.
 */
export const DbHealth = z.object({
    version: z.string().min(1).max(128),
    uptimeSeconds: z.number().nonnegative(),
    role: DbReplicationRole.nullable(),
    connections: Count.nullable(),
    maxConnections: Count.nullable(),
    /**
     * **Cumulative since the engine last started**, which is how all three engines
     * that expose it keep it (MySQL `Aborted_connects`, MongoDB's per-mechanism
     * `received − successful`, Redis `acl_access_denied_auth`). The module takes the
     * difference, and a value going down is a restart rather than a negative rate.
     * PostgreSQL only writes failed logins to its log, so it is null there.
     */
    failedLoginsTotal: Count.nullable(),
    /** Seconds behind the primary. Null on anything that is not a replica. */
    replicationLagSeconds: z.number().nonnegative().nullable(),
    /** Queries running longer than the threshold asked for, right now. */
    longRunningQueries: Count.nullable(),
    /** The engine's own memory, where it reports one (MongoDB, Redis). */
    memoryBytes: Bytes.nullable(),
});
/**
 * Which product answered, because the engine does not decide support dates: MySQL
 * and MariaDB are one engine to connect to and two release calendars, and Valkey
 * answers as Redis.
 */
export const DbProduct = z.enum(['postgresql', 'mysql', 'mariadb', 'mongodb', 'redis', 'valkey']);
/**
 * The engine's own account of how it can be reached (section 8.6).
 *
 * Only what the engine says about itself. Whether a port is actually open on a
 * public interface is a question for the operating system's socket table, which the
 * `db_exposure` module asks directly; this is the other half, and the two disagree
 * usefully when a firewall or a container sits in between.
 */
export const DbExposure = z.object({
    product: DbProduct,
    version: z.string().min(1).max(128),
    /** The release line support dates attach to: `17`, `8.4`, `11.4`, `8.0`. */
    series: z.string().regex(/^\d{1,3}(\.\d{1,3})?$/),
    /**
     * What the engine is configured to bind, in its own words (`*`, `0.0.0.0`,
     * `localhost`). Null when this account may not read it; empty when it listens on
     * a Unix socket only.
     */
    bindAddresses: z.array(z.string().min(1).max(255)).max(64).nullable(),
    port: z.number().int().positive().max(65535).nullable(),
    /**
     * Whether the engine demands a password. Null when it will not say to this
     * account: PostgreSQL keeps `pg_hba.conf` from everyone but superusers, so the
     * module has to find out another way.
     */
    authRequired: z.boolean().nullable(),
});
//# sourceMappingURL=db-driver.js.map